/**
 * Service de carrinho (Previsão e Pedido)
 */

import { api } from './api';
import {
  API_ENDPOINTS,
  CarrinhoPrevisaoRequest,
  CarrinhoPedidoRequest,
  CarrinhoResponse,
  ItemCarrinhoPrevisao,
  ItemCarrinhoPedido,
  CarrinhoState,
  ProdutoPrevisaoVendedor,
  ObterProdutoPrevisaoVendedorRequest,
} from '@types';
import { SessionUtil, CarrinhoUtil } from '@utils';

export class CarrinhoService {
  /**
   * Envia carrinho de previsão
   */
  async enviarPrevisao(carrinho: CarrinhoState): Promise<CarrinhoResponse> {
    try {
      const vendedor = SessionUtil.getVendedor();

      if (!vendedor) {
        throw new Error('Vendedor não encontrado na sessão');
      }

      // Valida carrinho
      const validation = CarrinhoUtil.validateCarrinho(carrinho);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Converte itens para formato da API
      const produtos: ItemCarrinhoPrevisao[] = CarrinhoUtil.toArray(carrinho).map(
        (item) => ({
          codigo: item.codigo,
          nome: item.nome,
          imagem: item.imagem,
          quantidade: item.quantidade,
          peso: item.peso,
          unidade: item.unidade,
          vendedor: vendedor.vendCodigo,
        })
      );

      const params: CarrinhoPrevisaoRequest = {
        previsao: vendedor.indPrevisao,
        previsaoIni: vendedor.indPrevisaoIni,
        previsaoFim: vendedor.indPrevisaoFim,
        vendedor: vendedor.vendCodigo,
        vendedorId: vendedor.vendId,
        produtos,
      };

      const response = await api.post<CarrinhoResponse>(
        API_ENDPOINTS.CAD_CAR_PREVISAO,
        params
      );

      return response;
    } catch (error) {
      console.error('Erro ao enviar previsão:', error);
      throw error;
    }
  }

  /**
   * Envia carrinho de pedido
   */
  async enviarPedido(carrinho: CarrinhoState): Promise<CarrinhoResponse> {
    try {
      const vendedor = SessionUtil.getVendedor();
      const cliente = SessionUtil.getCliente();

      if (!vendedor) {
        throw new Error('Vendedor não encontrado na sessão');
      }

      if (!cliente) {
        throw new Error('Cliente não selecionado');
      }

      // Valida carrinho
      const validation = CarrinhoUtil.validateCarrinho(carrinho);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Converte itens para formato da API
      const produtos: ItemCarrinhoPedido[] = CarrinhoUtil.toArray(carrinho).map(
        (item) => ({
          codigo: item.codigo,
          quantidade: item.quantidade,
          qtde: item.quantidade,
          peso: item.peso || 0,
          valor: item.valor || 0, // Valor unitário
          total: item.total || (item.valor || 0) * item.quantidade, // Total = quantidade * valor
          vendedor: vendedor.vendCodigo,
          cliente: cliente.cliCodigo,
          loja: 'loja' in item ? item.loja : 0, // 0 = estoque normal, 1 = estoque loja
          estoque: 'estoque' in item ? item.estoque : 0,
        })
      );

      const params: CarrinhoPedidoRequest = {
        previsao: vendedor.indPrevisao,
        cliente: cliente.cliCodigo,
        vendedor: vendedor.vendCodigo,
        total: carrinho.total,
        peso: carrinho.pesoTotal,
        produtos,
      };

      const response = await api.post<CarrinhoResponse>(
        API_ENDPOINTS.CAD_CAR_PEDIDO,
        params
      );

      return response;
    } catch (error) {
      console.error('Erro ao enviar pedido:', error);
      throw error;
    }
  }

  /**
   * Envia carrinho (detecta tipo automaticamente)
   */
  async enviar(carrinho: CarrinhoState): Promise<CarrinhoResponse> {
    if (carrinho.tipo === 'previsao') {
      return this.enviarPrevisao(carrinho);
    } else {
      return this.enviarPedido(carrinho);
    }
  }

  /**
   * Carrega carrinho da sessão
   */
  loadCarrinho(tipo: 'previsao' | 'pedido'): CarrinhoState {
    return CarrinhoUtil.loadCarrinho(tipo);
  }

  /**
   * Salva carrinho na sessão
   */
  saveCarrinho(carrinho: CarrinhoState): void {
    CarrinhoUtil.saveCarrinho(carrinho);
  }

  /**
   * Limpa carrinho
   */
  clearCarrinho(tipo: 'previsao' | 'pedido'): void {
    CarrinhoUtil.clearCarrinho(tipo);
  }

  /**
   * Verifica se carrinho está vazio
   */
  isEmpty(carrinho: CarrinhoState): boolean {
    return CarrinhoUtil.isEmpty(carrinho);
  }

  /**
   * Obtém total de itens
   */
  getTotalItems(carrinho: CarrinhoState): number {
    return CarrinhoUtil.getTotalItems(carrinho);
  }

  /**
   * Obtém resumo do carrinho
   */
  getResumo(carrinho: CarrinhoState): {
    total: number;
    pesoTotal: number;
    quantidadeTotal: number;
    totalItems: number;
    tipo: 'previsao' | 'pedido';
  } {
    return {
      total: carrinho.total,
      pesoTotal: carrinho.pesoTotal,
      quantidadeTotal: carrinho.quantidadeTotal,
      totalItems: this.getTotalItems(carrinho),
      tipo: carrinho.tipo,
    };
  }

  /**
   * Obtém produtos da previsão ativa do vendedor
   */
  async obterProdutosPrevisaoVendedor(
    previsao: number,
    vendedor: string
  ): Promise<ProdutoPrevisaoVendedor[]> {
    try {
      const params: ObterProdutoPrevisaoVendedorRequest = {
        previsao,
        vendedor,
      };

      console.log('[CARRINHO] Chamando API obterProdutoPrevisaoVendedor:', {
        endpoint: API_ENDPOINTS.OBTER_PRODUTO_PREVISAO_VENDEDOR,
        params,
      });

      const response = await api.post<ProdutoPrevisaoVendedor[]>(
        API_ENDPOINTS.OBTER_PRODUTO_PREVISAO_VENDEDOR,
        params
      );

      console.log('[CARRINHO] Resposta da API:', response);

      return response || [];
    } catch (error) {
      console.error('[CARRINHO] Erro ao obter produtos da previsão:', error);
      return [];
    }
  }
}

export const carrinhoService = new CarrinhoService();